import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { StatusBadge } from "@/components/Common";
import FileManager from "@/components/FileManager";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, User, CalendarCheck, CheckCircle2, Clock,
  MessageSquarePlus, Send, Package,
} from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [fuNote, setFuNote] = useState("");
  const [fuChannel, setFuChannel] = useState("Call");

  const load = async () => {
    const [c, n] = await Promise.all([
      api.get(`/clients/${id}`), api.get(`/clients/${id}/notes`),
    ]);
    setClient(c.data); setNotes(n.data);
  };
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/clients/${id}/notes`, { text: noteText });
      setNoteText(""); toast.success("Note added"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const logFollowup = async () => {
    try {
      await api.post("/followups", { client_id: id, note: fuNote, channel: fuChannel });
      setFuNote(""); toast.success("Follow-up logged for this week"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!client) return <div className="h-40" />;

  return (
    <>
      <Button variant="ghost" className="mb-4 rounded-sm gap-2 -ml-2" onClick={() => nav("/customers")} data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back to customers
      </Button>

      <div className="border border-border bg-card rounded-sm p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-1">Customer</p>
            <h1 className="text-3xl font-display font-black tracking-tighter" data-testid="customer-title">{client.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {client.company || "—"}</span>
              <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> POC: {client.poc?.name || "Unassigned"}</span>
              <span className="flex items-center gap-1.5"><Package className="h-4 w-4" /> {client.order_count} orders</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={client.status} />
            {client.followup_done ? (
              <span className="inline-flex items-center gap-1 text-success text-xs font-bold border border-success/40 bg-success/10 px-2 py-1 rounded-sm">
                <CheckCircle2 className="h-3.5 w-3.5" /> Followed up
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[hsl(45_100%_32%)] text-xs font-bold border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2 py-1 rounded-sm">
                <Clock className="h-3.5 w-3.5" /> Follow-up pending
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: notes + followup */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-border bg-card rounded-sm p-6">
            <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2 mb-4">
              <CalendarCheck className="h-5 w-5 text-primary" /> Log Weekly Follow-up
            </h3>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-1">
                <Label className="text-xs">Channel</Label>
                <Select value={fuChannel} onValueChange={setFuChannel}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="followup-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Call", "Email", "WhatsApp", "Meeting"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3">
                <Label className="text-xs">Note (optional)</Label>
                <div className="flex gap-2 mt-1">
                  <Textarea value={fuNote} onChange={(e) => setFuNote(e.target.value)} rows={1}
                    placeholder="What was discussed / order status…" className="rounded-sm resize-none" data-testid="followup-note-input" />
                  <Button onClick={logFollowup} className="rounded-sm shrink-0 gap-1.5" data-testid="log-followup-button">
                    <CheckCircle2 className="h-4 w-4" /> Log
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm p-6">
            <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2 mb-4">
              <MessageSquarePlus className="h-5 w-5 text-primary" /> Customer Notes
            </h3>
            <div className="flex gap-2 mb-5">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                placeholder="Add a note — work done, order update, task completed…" className="rounded-sm resize-none" data-testid="note-input" />
              <Button onClick={addNote} disabled={!noteText.trim()} className="rounded-sm shrink-0 self-end gap-1.5" data-testid="add-note-button">
                <Send className="h-4 w-4" /> Add
              </Button>
            </div>
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-sm">No notes yet.</p>
            ) : (
              <div className="space-y-0 relative">
                {notes.map((n, i) => (
                  <div key={n.id} className="flex gap-3 pb-5 last:pb-0" data-testid={`note-${n.id}`}>
                    <div className="flex flex-col items-center">
                      <div className="h-8 w-8 bg-secondary flex items-center justify-center rounded-sm font-display font-bold text-xs shrink-0">
                        {n.author_name?.[0]?.toUpperCase()}
                      </div>
                      {i < notes.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                    </div>
                    <div className="flex-1 min-w-0 -mt-0.5">
                      <div className="flex items-baseline gap-2">
                        <p className="text-sm font-semibold">{n.author_name}</p>
                        <p className="text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      <p className="text-sm mt-0.5 whitespace-pre-wrap">{n.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: files */}
        <div className="border border-border bg-card rounded-sm p-6 h-fit">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Order Files</h3>
          <FileManager entityType="customer" entityId={id} />
        </div>
      </div>
    </>
  );
}
